// ProvidersDrawer.js
import { useState, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { Drawer, Button, toast } from '@bsf/force-ui';
import {
	LoaderCircle as LoaderIcon,
	ChevronLeft as ChevronLeftIcon,
} from 'lucide-react';
import ProviderList from '@screens/connections/provider-list';
import AWSForm, { awsDefaultValues, awsFormSchema } from '@forms/aws-form';
import OSMTPForm, {
	osmtpDefaultValues,
	osmtpFormSchema,
} from '@forms/osmtp-form';
import { testAndSaveEmailConnection as apiTestAndSaveEmailConnection } from '@api/connections'; // Import API functions
import { defaultConnectionTitles } from '@utils/utils';
import { CONNECTION_PROVIDERS_BY_VALUE } from './provider-list';

const ProvidersDrawer = ( {
	isOpen,
	setIsOpen,
	currentConnection = {},
	onSave,
	totalConnections = {},
	totalConnectionCount = 0,
} ) => {
	// Ensure totalConnections is an object and has counts for all providers
	const safeTotalConnections = Object.keys(
		CONNECTION_PROVIDERS_BY_VALUE
	).reduce( ( acc, provider ) => {
		acc[ provider ] = totalConnections[ provider ] || 0;
		return acc;
	}, {} );

	const [ selectedProvider, setSelectedProvider ] = useState(
		currentConnection ? currentConnection.type : ''
	);
	const [ formData, setFormData ] = useState( currentConnection );
	const [ errors, setErrors ] = useState( {} );

	const [ isLoading, setIsLoading ] = useState( false );
	// Add form refs
	const formRef = useRef( null );

	useEffect( () => {
		if ( currentConnection && currentConnection.type ) {
			setSelectedProvider( currentConnection.type );
			setFormData( currentConnection );
		}
	}, [ currentConnection ] );

	useEffect( () => {
		if ( ! formData || Object.keys( formData ).length === 0 ) {
			const defaultValues =
				selectedProvider === 'AWS'
					? awsDefaultValues
					: osmtpDefaultValues;

			// Get the current count for the selected provider and add 1 for priority
			const currentProviderCount =
				safeTotalConnections[ selectedProvider ] || 0;
			const initialPriority = currentProviderCount + 1;

			const defaultTitle =
				initialPriority === 1
					? defaultConnectionTitles[ selectedProvider ]
					: `${ defaultConnectionTitles[ selectedProvider ] } (${
							initialPriority - 1
					  })`;

			setFormData( {
				...defaultValues,
				connection_title: defaultTitle,
				priority: initialPriority,
			} );
		}
	}, [ selectedProvider, formData, safeTotalConnections ] );

	const handleProviderSelect = ( provider ) => {
		setSelectedProvider( provider );
		const defaultValues =
			provider === 'AWS' ? awsDefaultValues : osmtpDefaultValues;

		// Get the current count for the selected provider and add 1 for priority
		const currentProviderCount = safeTotalConnections[ provider ] || 0;
		const initialPriority = currentProviderCount + 1;

		const defaultTitle =
			initialPriority === 1
				? defaultConnectionTitles[ provider ]
				: `${ defaultConnectionTitles[ provider ] } (${
						initialPriority - 1
				  })`;

		setFormData( {
			...defaultValues,
			connection_title: defaultTitle,
			priority: totalConnectionCount + 1,
		} );
		setErrors( {} );
	};

	// On blur, validate the input.
	const handleOnBlurValidation = ( event ) => {
		if ( ! event.target ) {
			return;
		}
		if ( ! event?.target ) {
			return;
		}
		const field = event.target?.name;
		const schema =
			selectedProvider === 'AWS' ? awsFormSchema : osmtpFormSchema;
		try {
			schema.pick( { [ field ]: true } ).parse( {
				[ field ]: formData[ field ],
			} );
			setErrors( ( prev ) => ( {
				...prev,
				[ field ]: undefined,
			} ) );
		} catch ( error ) {
			setErrors( ( prev ) => ( {
				...prev,
				[ field ]: error.errors[ 0 ].message,
			} ) );
		}
	};

	const handleSetOpenDrawer = ( value ) => {
		setIsOpen( value );
		if ( ! value ) {
			setSelectedProvider( '' );
			setErrors( {} );
			setFormData( {} );
		}
	};

	const validateForm = () => {
		try {
			const schema =
				selectedProvider === 'AWS' ? awsFormSchema : osmtpFormSchema;
			schema.parse( formData );
			setErrors( {} );
			return true;
		} catch ( error ) {
			const formattedErrors = {};
			error.errors.forEach( ( err ) => {
				formattedErrors[ err.path[ 0 ] ] = err.message;
			} );
			setErrors( formattedErrors );

			// Focus the first input with error
			const firstErrorField = error.errors[ 0 ]?.path[ 0 ];
			const firstErrorInput = formRef.current?.querySelector(
				`input[name="${ firstErrorField }"]`
			);
			firstErrorInput?.focus();

			return false;
		}
	};

	const hasChanges =
		JSON.stringify( formData ) !== JSON.stringify( currentConnection );

	const handleSaveChanges = async () => {
		if ( ! hasChanges ) {
			toast.info( __( 'No changes to save.', 'suremails' ) );
			return;
		}

		if ( ! validateForm() ) {
			return;
		}

		const payload = {
			settings: formData,
			provider: selectedProvider.toUpperCase(),
		};
		setIsLoading( true );

		try {
			const response = await apiTestAndSaveEmailConnection( payload );

			if ( response?.success ) {
				toast.success( __( 'Verification successful!', 'suremails' ), {
					description: __(
						'Connection tested and saved successfully!',
						'suremails'
					),
				} );
				setIsOpen( false ); // Close drawer on success
				onSave( response.connection );
			} else {
				toast.error( __( 'Verification Failed!', 'suremails' ), {
					description: response.message,
					autoDismiss: false,
				} );
			}
		} catch ( error ) {
			toast.error( __( 'Verification Failed!', 'suremails' ), {
				description:
					error.message ||
					__(
						'An unexpected error occurred while testing the connection.',
						'suremails'
					),
				autoDismiss: false,
			} );
		} finally {
			setIsLoading( false );
		}
	};

	/**
	 * Captures form data from AWSForm or OSMTPForm.
	 *
	 * @param {Object} data - The form data.
	 */
	const handleFormSubmit = ( data ) => {
		const [ field, value ] = Object.entries( data )[ 0 ];
		setFormData( ( prev ) => ( {
			...prev,
			[ field ]: value,
		} ) );

		// Clear error only for the field being changed
		setErrors( ( prev ) => ( {
			...prev,
			[ field ]: undefined,
		} ) );
	};

	// Define drawer title and description based on selected provider
	const title = selectedProvider
		? __( 'Connection Details', 'suremails' )
		: __( 'New Connection', 'suremails' );
	const description = selectedProvider
		? __(
				'Enter the details below to connect with your {providerName} account.',
				'suremails'
		  ).replace(
				'{providerName}',
				selectedProvider === 'AWS' ? 'Amazon SES' : selectedProvider
		  )
		: __(
				'Pick an email provider to ensure your WordPress emails are delivered securely and reliably.',
				'suremails'
		  );

	return (
		<Drawer
			design="footer-bordered"
			exitOnEsc
			position="right"
			scrollLock
			transitionDuration={ 0.2 }
			open={ isOpen }
			setOpen={ handleSetOpenDrawer }
			className="z-999999"
		>
			<Drawer.Backdrop />
			<form ref={ formRef } noValidate>
				<Drawer.Panel className="w-[34.75rem]">
					<Drawer.Header>
						<div className="flex items-center justify-between text-text-primary">
							<Drawer.Title>{ title }</Drawer.Title>
							<Drawer.CloseButton type="button" />
						</div>
						<Drawer.Description className="text-sm font-normal text-text-secondary">
							{ description }
						</Drawer.Description>
					</Drawer.Header>
					<Drawer.Body className="overflow-x-hidden">
						{ selectedProvider ? (
							<div>
								{ selectedProvider === 'AWS' && (
									<AWSForm
										onChange={ handleFormSubmit }
										connectionData={ formData }
										errors={ errors }
										inlineValidator={
											handleOnBlurValidation
										}
									/>
								) }
								{ selectedProvider === 'SMTP' && (
									<OSMTPForm
										onChange={ handleFormSubmit }
										connectionData={ formData }
										errors={ errors }
										inlineValidator={
											handleOnBlurValidation
										}
									/>
								) }
							</div>
						) : (
							<ProviderList
								onSelectProvider={ handleProviderSelect }
							/>
						) }
					</Drawer.Body>
					{ selectedProvider && (
						<Drawer.Footer>
							<Button
								onClick={ () => setSelectedProvider( '' ) }
								variant="outline"
								icon={ <ChevronLeftIcon /> }
								size="sm"
								iconPosition="left"
								className="font-medium"
								type="button"
							>
								{ __( 'Back', 'suremails' ) }
							</Button>
							<Button
								variant="primary"
								loading={ isLoading }
								icon={
									isLoading ? (
										<LoaderIcon className="animate-spin" />
									) : null
								}
								onClick={ handleSaveChanges }
								className="font-medium"
								size="sm"
								type="button"
							>
								{ isLoading
									? __( 'Testing…', 'suremails' )
									: __( 'Save Changes', 'suremails' ) }
							</Button>
						</Drawer.Footer>
					) }
				</Drawer.Panel>
			</form>
		</Drawer>
	);
};

export default ProvidersDrawer;
